/**
 * Round-trip context for the shared list selector, persisted in sessionStorage
 * so the "Create a list" detour can restore the originating page.
 *
 * IMPORTANT: this context is written to sessionStorage in clear text. Never
 * store secrets, tokens, or other sensitive data here — keep it limited to
 * navigation state and form values already visible in the UI.
 */
const STORAGE_KEY = 'oms:listSelectorReturnContext'

/**
 * Maximum age, in milliseconds, before a stored return context is considered
 * stale and discarded. Prevents an abandoned "Create a list" flow from
 * leaking into a later, unrelated list creation.
 */
const CONTEXT_TTL_MS = 15 * 60 * 1000 // 15 minutes

/**
 * Persists the round-trip return context used by the shared list selector.
 * Stamps a fresh `savedAt` timestamp on every write so the TTL window is
 * refreshed each time the context is saved or updated.
 * @param {object} ctx - The context to store.
 * @returns {void}
 */
export function saveContext(ctx) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...ctx, savedAt: Date.now() }))
}

/**
 * Reads the stored return context. Tolerates missing or corrupt data, and
 * discards the context once it is older than {@link CONTEXT_TTL_MS}.
 * @returns {object|null} The parsed context, or null when absent/invalid/stale.
 */
export function readContext() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed.savedAt || Date.now() - parsed.savedAt > CONTEXT_TTL_MS) {
      clearContext()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/**
 * Merges the newly created list id into the stored context.
 * No-op when no context is stored.
 * @param {string} id - The created list id.
 * @returns {void}
 */
export function setCreatedListId(id) {
  const ctx = readContext()
  if (!ctx) return
  ctx.createdListId = id
  saveContext(ctx)
}

/**
 * Removes the stored return context.
 * @returns {void}
 */
export function clearContext() {
  sessionStorage.removeItem(STORAGE_KEY)
}
